"use client";

import { useScrollAnimate } from "../lib/hooks";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const ORANGE = "#F97316";
const GREEN = "#22c55e";
const GRAY = "#94a3b8";
const CYAN = "#06b6d4";
const AMBER = "#f59e0b";
const BLUE = "#60A5FA";
const PURPLE = "#a78bfa";
const MK_BG = "#0f0f11";
const MK_CARD = "rgba(255,255,255,0.04)";
const MK_BD = "rgba(255,255,255,0.06)";

const CHANNELS = [
  { name: "Google Search", visits: 1400, pct: 54, color: GREEN },
  { name: "Direct", visits: 480, pct: 18, color: GRAY },
  { name: "ChatGPT (paid)", visits: 240, pct: 9, color: ORANGE },
  { name: "ChatGPT (organic)", visits: 220, pct: 8, color: TEAL },
  { name: "Perplexity", visits: 127, pct: 5, color: CYAN },
  { name: "Claude", visits: 79, pct: 3, color: AMBER },
  { name: "Google AI", visits: 38, pct: 1, color: BLUE },
  { name: "Copilot", visits: 16, pct: 1, color: PURPLE },
];

const TOTAL_SESSIONS = CHANNELS.reduce((sum, c) => sum + c.visits, 0);

// 16 data points (every 2 days) across 30 days (Apr 1 – Apr 30). Launch at i=7 (Apr 15).
const xs = [40, 76, 112, 148, 184, 220, 256, 292, 328, 364, 400, 436, 472, 508, 544, 580];

// Y mapping: 0..70 → 220..30 (190 vertical pixels). y = 220 - val * (190/70) = 220 - val * 2.714
const yFor = (val) => Math.round(220 - val * (190 / 70));

const seriesGoogleSearch = [42, 45, 48, 50, 44, 48, 52, 50, 50, 54, 52, 58, 54, 60, 56, 58];
const seriesDirect = [14, 15, 17, 16, 18, 14, 16, 17, 17, 18, 16, 18, 17, 19, 18, 17];
const seriesChatGPTOrg = [4, 4, 5, 5, 6, 6, 7, 7, 7, 8, 8, 9, 10, 11, 12, 13];
const seriesPerplexity = [3, 4, 4, 5, 4, 4, 4, 5, 5, 5, 4, 4, 5, 4, 5, 5];
// Paid: 0 pre-launch; ramp post-launch (i=7..15)
const seriesPaidPostLaunch = [0, 4, 10, 18, 26, 34, 42, 50, 50]; // 9 values from i=7 (launch baseline at 0) to i=15

function pathFromSeries(series) {
  return series.map((v, i) => `${i === 0 ? "M" : "L"} ${xs[i]},${yFor(v)}`).join(" ");
}

function pathFromPaid() {
  // Start at launch x=292 (i=7) at baseline y=220, then ramp through i=8..15
  const out = [`M ${xs[7]},220`];
  for (let j = 1; j < seriesPaidPostLaunch.length; j++) {
    out.push(`L ${xs[7 + j - 1] + 0},${yFor(seriesPaidPostLaunch[j])}`); // wait, off by one
  }
  return out.join(" ");
}

// Build paid path correctly: i=7..15 inclusive (9 points), values are seriesPaidPostLaunch
// First point at (xs[7], 220) is the launch baseline; subsequent points at (xs[8..15], y for values 1..8)
function paidPath() {
  const points = [`M ${xs[7]},220`];
  // Map: i=8 → seriesPaidPostLaunch[1], i=9 → [2], ..., i=15 → [8]
  for (let i = 8; i <= 15; i++) {
    const val = seriesPaidPostLaunch[i - 7];
    points.push(`L ${xs[i]},${yFor(val)}`);
  }
  return points.join(" ");
}

const fmtN = (n) => n.toLocaleString("en-US");

export default function AIAnalyticsOverviewMockup() {
  const [ref, scrolled] = useScrollAnimate();

  return (
    <div
      ref={ref}
      style={{
        margin: "32px 0",
        opacity: scrolled ? 1 : 0,
        transform: scrolled ? "translateY(0)" : "translateY(8px)",
        transition: "opacity 0.5s ease, transform 0.5s ease",
        background: MK_BG,
        borderRadius: 12,
        border: `1px solid ${MK_BD}`,
        padding: 0,
        fontFamily: "var(--body)",
        overflow: "hidden",
      }}
    >
      {/* Module header bar */}
      <div style={{ padding: "20px 24px 0", borderBottom: `1px solid ${MK_BD}` }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, letterSpacing: "-0.01em", marginBottom: 4 }}>
              AI Analytics
            </div>
            <div style={{ fontSize: 12, color: TEXT_DIM, lineHeight: 1.5 }}>
              Track AI bot activity and referral traffic on your site
            </div>
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: MK_CARD,
              border: `1px solid ${MK_BD}`,
              borderRadius: 6,
              padding: "6px 10px",
              fontSize: 11,
              color: TEXT_DIM,
              fontFamily: "var(--mono)",
              whiteSpace: "nowrap",
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Apr 1 – Apr 30
          </div>
        </div>

        {/* Tab strip */}
        <div style={{ display: "flex", gap: 24, fontSize: 13, paddingBottom: 0 }}>
          {[
            { label: "Overview", active: true },
            { label: "Bot Activity", active: false },
            { label: "Referral Traffic", active: false },
          ].map((t) => (
            <div
              key={t.label}
              style={{
                paddingBottom: 12,
                fontWeight: t.active ? 600 : 500,
                color: t.active ? TEAL : TEXT_DIM,
                borderBottom: t.active ? `2px solid ${TEAL}` : "2px solid transparent",
              }}
            >
              {t.label}
            </div>
          ))}
        </div>
      </div>

      {/* Two-card grid */}
      <div
        style={{
          padding: "20px 24px 24px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 16,
        }}
      >
        {/* LEFT: Channels list */}
        <div
          style={{
            background: MK_CARD,
            border: `1px solid ${MK_BD}`,
            borderRadius: 10,
            padding: "16px 18px",
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 4 }}>Channels</div>
          <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 14 }}>
            All traffic sources, last 30 days
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {CHANNELS.map((c) => (
              <li
                key={c.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                  fontSize: 13,
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: c.color,
                      flexShrink: 0,
                      display: "inline-block",
                    }}
                  />
                  <span style={{ color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.name}
                  </span>
                </span>
                <span style={{ display: "inline-flex", alignItems: "baseline", gap: 10, fontFamily: "var(--mono)", fontVariantNumeric: "tabular-nums" }}>
                  <span style={{ color: TEXT, fontWeight: 600 }}>{fmtN(c.visits)}</span>
                  <span style={{ color: TEXT_DIM, fontSize: 11, minWidth: 32, textAlign: "right" }}>{c.pct}%</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT: Sessions over time */}
        <div
          style={{
            background: MK_CARD,
            border: `1px solid ${MK_BD}`,
            borderRadius: 10,
            padding: "16px 18px",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 4 }}>Sessions over time</div>
              <div style={{ fontSize: 11, color: TEXT_DIM }}>By channel, last 30 days</div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: TEXT, fontFamily: "var(--mono)", lineHeight: 1 }}>
              {fmtN(TOTAL_SESSIONS)}
            </div>
          </div>

          <div className="hide-scrollbar" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px" }}>
            <svg
              viewBox="0 0 600 260"
              xmlns="http://www.w3.org/2000/svg"
              style={{ width: "100%", minWidth: 480, height: "auto", display: "block" }}
              role="img"
              aria-label="Multi-line chart of daily sessions across 30 days. Five lines: Google Search organic in green is the largest at roughly 50 per day. Direct in gray is steady at roughly 17 per day. ChatGPT paid in orange is zero for the first 14 days then ramps from 4 to 50 per day after a campaign launch on April 15. ChatGPT organic in teal grows from 4 to 13 per day, accelerating after the launch. Perplexity in cyan stays steady around 4 to 5 per day."
            >
              {/* Y-axis gridlines */}
              {[0, 20, 40, 60].map((v) => {
                const y = yFor(v);
                return (
                  <g key={v}>
                    <line x1="40" y1={y} x2="580" y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                    <text x="32" y={y + 3} fill="#a0a0a0" fontSize="9" textAnchor="end" fontFamily="monospace">
                      {v}
                    </text>
                  </g>
                );
              })}

              {/* Launch annotation at x=292 (i=7, Apr 15) */}
              <line x1="292" y1="20" x2="292" y2="220" stroke="rgba(255,255,255,0.25)" strokeDasharray="3,3" strokeWidth="1" />
              <text x="292" y="14" fill={TEXT} fontSize="9" textAnchor="middle" fontFamily="var(--body)" fontWeight="600">
                Apr 15: Campaign launch
              </text>

              {/* Lines (drawn back-to-front) */}
              <path d={pathFromSeries(seriesGoogleSearch)} fill="none" stroke={GREEN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
              <path d={pathFromSeries(seriesDirect)} fill="none" stroke={GRAY} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
              <path d={pathFromSeries(seriesPerplexity)} fill="none" stroke={CYAN} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
              <path d={pathFromSeries(seriesChatGPTOrg)} fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d={paidPath()} fill="none" stroke={ORANGE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

              {/* End-of-line markers */}
              <circle cx="580" cy={yFor(58)} r="3.5" fill={GREEN} stroke="#0f0f11" strokeWidth="2" />
              <circle cx="580" cy={yFor(50)} r="3.5" fill={ORANGE} stroke="#0f0f11" strokeWidth="2" />
              <circle cx="580" cy={yFor(13)} r="3" fill={TEAL} stroke="#0f0f11" strokeWidth="2" />
              <circle cx="580" cy={yFor(17)} r="2.5" fill={GRAY} stroke="#0f0f11" strokeWidth="1.5" />
              <circle cx="580" cy={yFor(5)} r="2.5" fill={CYAN} stroke="#0f0f11" strokeWidth="1.5" />

              {/* X-axis labels */}
              {[
                { day: 1, x: 40, label: "Apr 1" },
                { day: 7, x: 148, label: "Apr 7" },
                { day: 15, x: 292, label: "Apr 15" },
                { day: 22, x: 436, label: "Apr 22" },
                { day: 30, x: 580, label: "Apr 30" },
              ].map((t) => (
                <text key={t.day} x={t.x} y="245" fill="#a0a0a0" fontSize="9" textAnchor="middle" fontFamily="monospace">
                  {t.label}
                </text>
              ))}
            </svg>
          </div>

          {/* Inline legend */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px 16px",
              marginTop: 8,
              fontSize: 10,
              color: TEXT_DIM,
              fontFamily: "var(--body)",
            }}
          >
            {[
              { name: "Google Search", color: GREEN },
              { name: "ChatGPT (paid)", color: ORANGE },
              { name: "ChatGPT (organic)", color: TEAL },
              { name: "Direct", color: GRAY },
              { name: "Perplexity", color: CYAN },
            ].map((s) => (
              <span key={s.name} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, display: "inline-block" }} />
                {s.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Caption + footnote */}
      <div style={{ padding: "0 24px 18px" }}>
        <div
          style={{
            padding: "12px 14px",
            borderLeft: `3px solid ${TEAL}`,
            background: "rgba(79,205,196,0.06)",
            borderRadius: 4,
            fontSize: 11,
            color: TEXT,
            lineHeight: 1.6,
          }}
        >
          <strong style={{ color: TEXT, fontWeight: 700 }}>What this view shows:</strong> all traffic sources stitched together. Paid ChatGPT drives the visible volume after launch, but organic ChatGPT also accelerates because the same Context hints are validated as AEO targets. Google Search and Direct are the existing baseline.
        </div>
      </div>
    </div>
  );
}
